import { useCallback } from 'react';
import { mainApi, getirApi, migrosApi, yemeksepetiApi } from '../../../../config/apiClient';
import NotificationService from '../../../../services/NotificationService';
import { PaymentType } from '../types';
import type { TrendyolUpdateResult } from './useTrendyolYemek';

/** Kurye panelinden gelen atama ayrıntıları (birlikte atama, paketi bölme, elle bölge/ücret). */
export interface CourierAssignExtras {
  order?: any;
  partNo?: number;
  partCount?: number;
  partNote?: string | null;
  collectsPayment?: boolean;
  zoneId?: number | null;
  feeOverride?: number | null;
}

export interface UseCourierHandlersDeps {
  selectedOrderId: number | null;
  selectedOrder: any;
  companyId: string | null;
  screenData: any;
  trendyolOrders: any[];
  setTrendyolOrders: React.Dispatch<React.SetStateAction<any[]>>;
  updateTrendyolOrderStatus: (packageId: string, action: 'accept' | 'ready' | 'shipped' | 'delivered') => Promise<TrendyolUpdateResult>;
  getirOrders: any[];
  setGetirOrders: React.Dispatch<React.SetStateAction<any[]>>;
  setMigrosOrders: React.Dispatch<React.SetStateAction<any[]>>;
  refreshGetirToken: () => void;
  fetchGetirOrders: () => Promise<void>;
  fetchYemeksepetiOrders: () => Promise<void>;
  handleUpdateOrderStatus: (orderId: number, newStatus: string, courierFirebaseUid?: string, courierName?: string) => Promise<void>;
  updateGetirOrderStatus: any;
  setSnackbar: (data: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
  setSelectCourierModalOpen: (open: boolean) => void;
  /** Paket listesini yeniler (bölünmüş pakette). */
  reloadOrders?: () => void;
}

export const useCourierHandlers = (deps: UseCourierHandlersDeps) => {
  const {
    selectedOrderId,
    selectedOrder,
    companyId,
    screenData,
    trendyolOrders,
    setTrendyolOrders,
    updateTrendyolOrderStatus,
    getirOrders,
    setGetirOrders,
    setMigrosOrders,
    refreshGetirToken,
    fetchGetirOrders,
    fetchYemeksepetiOrders,
    handleUpdateOrderStatus,
    updateGetirOrderStatus,
    setSnackbar,
    setSelectCourierModalOpen,
    reloadOrders,
  } = deps;

  const handleCourierSelect = useCallback(async (
    courierId: number,
    courierFirebaseUid: string,
    courierName: string,
    extras?: CourierAssignExtras,
  ) => {
    // Kurye paneli birden çok siparişi atarken siparişi kendisi verir
    const order = extras?.order ?? selectedOrder;
    const orderId = extras?.order?.id ?? selectedOrderId;
    const isSplit = (extras?.partCount ?? 1) > 1;
    const isFirstPart = (extras?.partNo ?? 1) === 1;
    if (!orderId || !order || !companyId) return;
    
    console.log('🛵 [CourierSelect] Kurye seçildi:', { courierId, courierFirebaseUid, courierName, orderId: orderId });
    
    try {
      const mapPaymentTypeToApi = (paymentType: PaymentType): string => {
        switch (paymentType) {
          case 'card': return 'POS';
          case 'online': return 'ONLINE';
          case 'cash':
          default: return 'CASH';
        }
      };

      // Trendyol siparişleri için önce platform-spesifik işlemler (Shipped durumu)
      if (order.source === 'trendyol' && isFirstPart) {
        const trdPackageId = order.trendyolPackageId;
        if (trdPackageId) {
          try {
            const { updateStatusInSql } = await import('../services/TrendyolYemekService');
            const trdOrderData = trendyolOrders.find(o => o.id === trdPackageId);
            await updateStatusInSql(trdPackageId, 'Shipped', {
              courierId,
              courierName,
              courierUid: courierFirebaseUid,
            }, trdOrderData);
            console.log('[CourierSelect] Trendyol SQL guncellendi (Shipped + kurye)');
          } catch (err) {
            console.warn('[CourierSelect] Trendyol SQL guncelleme hatasi:', err);
          }

          try {
            const shipResult = await updateTrendyolOrderStatus(trdPackageId, 'shipped');
            if (shipResult.success) {
              console.log('[CourierSelect] Trendyol API markShipped basarili');
            } else if (shipResult.statusMismatch) {
              // Self-heal: yerel state hook tarafından senkronlandı, kurye atama akışı kesilmesin.
              console.warn('[CourierSelect] Trendyol API markShipped — durum uyuşmazlığı:', shipResult.currentStatus);
            } else {
              console.warn('[CourierSelect] Trendyol API markShipped basarisiz:', shipResult.message);
            }
          } catch (err) {
            console.warn('[CourierSelect] Trendyol API markShipped hatasi:', err);
          }

          setTrendyolOrders(prev => prev.map(o =>
            o.id === trdPackageId ? { ...o, packageStatus: 'Shipped' } : o
          ));
        }
      }

      // orderSource ve platform ID'leri belirle
      const orderSource = order.source === 'getir' ? 'getir'
        : order.source === 'migros' ? 'migros'
        : order.source === 'trendyol' ? 'trendyol'
        : order.source === 'yemeksepeti' ? 'yemeksepeti'
        : 'package';

      const trdOrder = order.source === 'trendyol'
        ? trendyolOrders.find(o => o.id === order.trendyolPackageId)
        : null;

      const assignRequest = {
        packageOrderId: orderId,
        courierId,
        courierFirebaseUid,
        companyId,
        branchId: screenData?.branchId || null,
        orderNumber: order.shortOrderNo || `PKT-${orderId}`,
        customerName: order.customerName || 'Müşteri',
        customerPhone: order.customerPhone || '',
        deliveryAddress: order.customerAddress || '',
        totalAmount: order.totalAmount || 0,
        itemCount: order.itemCount || 0,
        notes: null,
        paymentMethod: mapPaymentTypeToApi(order.paymentType),
        orderSource,
        getirOrderId: orderSource === 'getir' ? orderId : null,
        migrosOrderId: orderSource === 'migros' ? orderId : null,
        trendyolPackageId: orderSource === 'trendyol' ? order.trendyolPackageId : null,
        trendyolSupplierId: orderSource === 'trendyol' && trdOrder ? String(trdOrder.supplierId) : null,
        trendyolDeliveryType: orderSource === 'trendyol' ? order.trendyolDeliveryType : null,
        yemeksepetiOrderId: orderSource === 'yemeksepeti' ? orderId : null,
        // Kurye paneli: paketi bölme, elle bölge/ücret
        partNo: extras?.partNo ?? 1,
        partCount: extras?.partCount ?? 1,
        partNote: extras?.partNote ?? null,
        collectsPayment: extras?.collectsPayment ?? true,
        zoneId: extras?.zoneId ?? null,
        feeOverride: extras?.feeOverride ?? null,
      };

      console.log('📤 [CourierSelect] CourierActiveOrders API çağrılıyor:', assignRequest);

      const assignResponse = await mainApi.post('/api/CourierActiveOrders/assign', assignRequest);
      const assignResult = assignResponse.data;

      if (assignResult.success) {
        console.log('✅ [CourierSelect] Kurye ataması başarılı:', assignResult);
        
        if (!isFirstPart) {
          // Bölünmüş paketin sonraki parçaları: platform ve durum işlemleri ilk parçada yapıldı
          reloadOrders?.();
        } else if (orderSource === 'getir') {
          console.log('🛵 [CourierSelect] Getir siparişi - kurye atandı, deliver bekliyor (TESLİM ET butonuyla)');
          
          try {
            await getirApi.post(`/api/getir-orders/${orderId}/assign-courier`, {
              courierId: courierId,
              courierName: courierName,
              courierFirebaseUid: courierFirebaseUid
            });
            console.log('✅ [CourierSelect] GetirOrders tablosu güncellendi (AssignedCourier)');
          } catch (err: any) {
            if (err?.response?.status === 401) {
              console.warn('⚠️ [CourierSelect] Token hatası (401) - token yenileme tetikleniyor...');
              refreshGetirToken();
            } else {
              console.warn('⚠️ [CourierSelect] GetirOrders AssignedCourier güncelleme hatası:', err);
            }
          }

          setGetirOrders(prev => prev.map(order => 
            order.id === orderId
              ? { ...order, assignedCourierId: courierId, assignedCourierName: courierName, assignedCourierFirebaseUid: courierFirebaseUid }
              : order
          ));
        } else if (orderSource === 'migros') {
          try {
            await migrosApi.post(`/api/migros-orders/${orderId}/assign-courier`, {
              CourierId: courierId,
              CourierName: courierName,
              CourierFirebaseUid: courierFirebaseUid
            });
            console.log('[CourierSelect] MigrosOrders tablosu guncellendi (AssignedCourier)');
          } catch (err) {
            console.warn('[CourierSelect] MigrosOrders AssignedCourier guncelleme hatasi:', err);
          }

          setMigrosOrders(prev => prev.map(order =>
            order.id === orderId
              ? { ...order, assignedCourierId: courierId, assignedCourierName: courierName, assignedCourierFirebaseUid: courierFirebaseUid }
              : order
          ));
        } else if (orderSource === 'yemeksepeti') {
          try {
            await yemeksepetiApi.post(`/api/ys-orders/${orderId}/assign-courier`, {
              CourierId: courierId,
              CourierName: courierName,
              CourierFirebaseUid: courierFirebaseUid
            });
            console.log('[CourierSelect] YemeksepetiOrders tablosu guncellendi (AssignedCourier)');
          } catch (err) {
            console.warn('[CourierSelect] YemeksepetiOrders AssignedCourier guncelleme hatasi:', err);
          }

          try {
            const pickupRes = (await yemeksepetiApi.post('/api/ys-orders/picked-up', {
              OrderId: orderId
            })).data;
            const pickupSuccess = pickupRes.Success ?? pickupRes.success;
            if (pickupSuccess) {
              console.log('[CourierSelect] YS picked-up otomatik guncellendi');
              setSnackbar({ open: true, message: `Kurye atandı ve kuryeye teslim edildi: ${courierName}`, severity: 'success' });
            }
          } catch (err) {
            console.warn('[CourierSelect] YS picked-up otomatik guncelleme hatasi:', err);
          }

          await fetchYemeksepetiOrders();
        } else if (orderSource === 'trendyol') {
          // Trendyol platform işlemleri zaten yukarıda yapıldı
        } else if (isSplit) {
          // Bölünmüş telefon siparişi: sunucu kurye adlarını birleştirip durumu günceller
          reloadOrders?.();
        } else {
          await handleUpdateOrderStatus(orderId, 'ON_DELIVERY', courierFirebaseUid, courierName);
        }
        
        try {
          console.log('📱 [CourierSelect] Kuryeye bildirim gönderiliyor:', courierFirebaseUid);
          
          const notificationTitle = 'Yeni Sipariş Atandı 🚀';
          const notificationBody = `Sipariş #${order.shortOrderNo || orderId} size atandı. Müşteri: ${order.customerName || 'Müşteri'}. Lütfen siparişi kabul edin.`;
          
          // .NET notification API (tek çağrı: kaydet + FCM gönder)
          const notifResult = await NotificationService.sendNotification(
            companyId,
            [courierFirebaseUid],
            notificationTitle,
            notificationBody
          );
          
          if (notifResult.success) {
            console.log('✅ [CourierSelect] Kuryeye bildirim gönderildi:', notifResult.sentCount);
          } else {
            console.warn('⚠️ [CourierSelect] Kuryeye bildirim gönderilemedi:', notifResult.message);
          }
        } catch (notifError) {
          console.error('⚠️ [CourierSelect] Bildirim gönderme hatası (kritik değil):', notifError);
        }
        
        setSnackbar({ 
          open: true, 
          message: `✅ Sipariş ${courierName} kuryesine atandı`, 
          severity: 'success' 
        });
      } else {
        console.error('❌ [CourierSelect] Kurye ataması başarısız:', assignResult);
        setSnackbar({ 
          open: true, 
          message: `❌ Kurye ataması başarısız: ${assignResult.message || 'Bilinmeyen hata'}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('❌ [CourierSelect] Kurye atama hatası:', error);
      setSnackbar({ 
        open: true, 
        message: `❌ Kurye atama hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 
        severity: 'error' 
      });
    }
  }, [selectedOrderId, selectedOrder, companyId, reloadOrders, screenData?.branchId, handleUpdateOrderStatus, updateGetirOrderStatus, fetchGetirOrders, setGetirOrders, setMigrosOrders, updateTrendyolOrderStatus, trendyolOrders, fetchYemeksepetiOrders]);

  return { handleCourierSelect };
};

export default useCourierHandlers;
